import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row, Input, Upload, message } from "antd";
import { IModalType } from "app/types/modalType";
import FormModal from "app/components/FormModal/FormModal";
import { FormItem } from "app/components/Form/Form";
import {
  FirmwareApiV2FirmwareTypeLatestGetRequest,
  FirmwareDTO,
  FirmwareType,
} from "@generated/v2";
import { UploadFile } from "antd/lib/upload/interface";
import Icon from "app/components/Icon/Icon";
import { useFirmwareUpload } from "app/screens/ReaderFirmwareScreen/hooks/useFirmwareUpload";
import useFetchData from "app/hooks/useFetchData";
import { readerFirmwareLatestVersionGet } from "app/api/v2/readerFirmwareApi";
import { gt as greaterVersion, valid as validVersion } from "semver";

const ALLOWED_FILE_TYPES: string[] = [
  "application/zip",
  "application/x-zip-compressed",
];

interface IProps extends IModalType {
  firmwareType: FirmwareType;
  initialized: boolean;
}

const ReaderFirmwareModal = ({
  show,
  close,
  submit,
  firmwareType,
  initialized,
}: IProps) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<UploadFile<any>>();

  const { data: latestsFirmware, loading: latestFirmwareLoading } =
    useFetchData<FirmwareDTO, FirmwareApiV2FirmwareTypeLatestGetRequest>(
      readerFirmwareLatestVersionGet,
      {
        params: { type: firmwareType },
        shouldCallApi: initialized,
      }
    );

  const latestVersion = latestsFirmware?.version;

  const { loading, upload } = useFirmwareUpload();

  // Memoized values

  const uploadFileProps = useMemo(
    () => ({
      onRemove: () => {
        setFile(undefined);
      },
      beforeUpload: (file: UploadFile<any>) => {
        setFile(file);
        return false;
      },
      fileList: file ? [file] : [],
    }),
    [file]
  );

  // Handlers

  /**
   * Handles submit
   * @param formData Inputted data from form
   */
  const handleSubmit = useCallback(
    (formData: Pick<FirmwareDTO, "version" | "description">) => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        message.error(t("readerFirmware.invalidFileError"));
        return;
      }

      // Validate version number
      if (!validVersion(formData.version)) {
        message.error(t("readerFirmware.invalidVersionError"));
        return;
      }

      if (latestVersion && greaterVersion(latestVersion, formData.version)) {
        message.error(t("readerFirmware.versionNumberLowError"));
        return;
      }

      upload({
        ...formData,
        firmwareType,
        file,
      }).then(() => {
        submit();
        close();
      });
    },
    [close, file, firmwareType, latestVersion, submit, t, upload]
  );

  const modalLocalization = useMemo(
    () => ({
      unsavedMessage: t("default.warningUnsavedChanges"),
    }),
    [t]
  );

  return (
    <FormModal
      header={t("readerFirmware.modalTitleCreate")}
      visible={show}
      onClose={close}
      onSubmit={handleSubmit}
      localization={modalLocalization}
      loadingSubmit={loading || latestFirmwareLoading}
      disabledSubmit={!file}
    >
      <Row gutter={24}>
        <Col span={12} md={18} sm={24}>
          <FormItem
            label={t("readerFirmware.inputNameLabel")}
            name="description"
          >
            <Input
              placeholder={t("readerFirmware.inputNamePlaceholder")}
              size="large"
            />
          </FormItem>
        </Col>
        <Col span={12} md={6} sm={24}>
          <FormItem
            label={t("readerFirmware.inputVersionLabel")}
            name="version"
            options={{
              rules: [
                {
                  required: true,
                  message: t("readerFirmware.inputVersionErrorRequired"),
                },
              ],
            }}
          >
            <Input
              placeholder={t("readerFirmware.inputVersionPlaceholder")}
              size="large"
            />
          </FormItem>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <Upload.Dragger {...uploadFileProps}>
            <p className="ant-upload-drag-icon">
              <Icon type="inbox" />
            </p>
            <p className="ant-upload-text">{t("default.dragFileToUpload")}</p>
          </Upload.Dragger>
        </Col>
      </Row>
    </FormModal>
  );
};

export default memo(ReaderFirmwareModal);
